import { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { ArrowLeftIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { clsx } from 'clsx';
import { Spinner } from '@theraply/ui';
import { usePublicAvailableSlots, type AvailableSlot } from '@theraply/api-client';

import { SlotSelector, type TimeSlot } from '../../components/booking/SlotSelector';
import { PaymentForm } from '../../components/booking/PaymentForm';
import { BookingConfirmation } from '../../components/booking/BookingConfirmation';
import { useCreateBooking, useConfirmBooking } from '../../hooks/useBooking';
import { useTherapistProfile } from '../../hooks/useTherapistProfile';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '');

type BookingStep = 'slot-selection' | 'notes' | 'payment' | 'confirmation';

interface BookingState {
  therapistId: number;
  selectedDate: string | null;
  selectedTime: string | null;
  sessionType: 'INTAKE' | 'REGULAR' | 'CRISIS';
  notes: string;
  paymentIntentId?: string;
  clientSecret?: string;
  amount?: number;
  platformFee?: number;
  therapistAmount?: number;
  bookingId?: number;
}

export function BookingPage() {
  const { therapistId } = useParams<{ therapistId: string }>();
  const navigate = useNavigate();
  const parsedTherapistId = parseInt(therapistId || '0');

  const [currentStep, setCurrentStep] = useState<BookingStep>('slot-selection');
  const [bookingState, setBookingState] = useState<BookingState>({
    therapistId: parsedTherapistId,
    selectedDate: null,
    selectedTime: null,
    sessionType: 'REGULAR',
    notes: '',
  });
  const [bookingError, setBookingError] = useState<string | null>(null);

  const createBookingMutation = useCreateBooking();
  const confirmBookingMutation = useConfirmBooking();

  // Fetch therapist profile
  const {
    data: therapist,
    isLoading: therapistLoading,
    error: therapistError,
  } = useTherapistProfile(parsedTherapistId);

  // Compute date range for availability (next 4 weeks)
  const startDate = useMemo(() => {
    const d = new Date();
    return d.toISOString().split('T')[0];
  }, []);

  const endDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 28);
    return d.toISOString().split('T')[0];
  }, []);

  // Fetch real availability slots
  const {
    data: availableSlotsList,
    isLoading: slotsLoading,
  } = usePublicAvailableSlots(parsedTherapistId, startDate, endDate);

  // Convert flat slot array to the Record<string, TimeSlot[]> format SlotSelector expects
  const availableSlots: Record<string, TimeSlot[]> = useMemo(() => {
    if (!availableSlotsList) return {};

    const grouped: Record<string, TimeSlot[]> = {};
    for (const slot of availableSlotsList) {
      if (!grouped[slot.date]) {
        grouped[slot.date] = [];
      }
      grouped[slot.date].push({
        time: slot.startTime,
        available: true,
      });
    }

    // Sort times within each date
    for (const date of Object.keys(grouped)) {
      grouped[date].sort((a, b) => a.time.localeCompare(b.time));
    }

    return grouped;
  }, [availableSlotsList]);

  const steps: { key: BookingStep; label: string }[] = [
    { key: 'slot-selection', label: 'Select Time' },
    { key: 'notes', label: 'Add Notes' },
    { key: 'payment', label: 'Payment' },
    { key: 'confirmation', label: 'Confirmation' },
  ];

  const currentStepIndex = steps.findIndex((s) => s.key === currentStep);

  const canProceed = (): boolean => {
    switch (currentStep) {
      case 'slot-selection':
        return !!(bookingState.selectedDate && bookingState.selectedTime);
      case 'notes':
        return true;
      case 'payment':
        return !!bookingState.clientSecret;
      default:
        return false;
    }
  };

  const handleNext = async () => {
    setBookingError(null);
    if (currentStep === 'slot-selection') {
      setCurrentStep('notes');
    } else if (currentStep === 'notes') {
      await handleCreateBooking();
    }
  };

  const handleBack = () => {
    setBookingError(null);
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      setCurrentStep(steps[prevIndex].key);
    }
  };

  const handleCreateBooking = async () => {
    if (!bookingState.selectedDate || !bookingState.selectedTime) {
      return;
    }

    try {
      setBookingError(null);
      const response = await createBookingMutation.mutateAsync({
        therapistId: bookingState.therapistId,
        slotDate: bookingState.selectedDate,
        slotTime: bookingState.selectedTime,
        sessionType: bookingState.sessionType,
        notes: bookingState.notes || undefined,
      });

      setBookingState((prev) => ({
        ...prev,
        bookingId: response.bookingId,
        clientSecret: response.clientSecret,
        amount: response.amount,
        platformFee: response.platformFee,
        therapistAmount: response.therapistAmount,
      }));

      setCurrentStep('payment');
    } catch (error: any) {
      setBookingError(
        error?.response?.data?.message ||
        error?.message ||
        'Failed to create booking. Please try again.'
      );
    }
  };

  const handlePaymentSuccess = async () => {
    if (!bookingState.bookingId) {
      return;
    }

    try {
      await confirmBookingMutation.mutateAsync(bookingState.bookingId);
      setCurrentStep('confirmation');
    } catch (error: any) {
      setBookingError(
        error?.response?.data?.message ||
        error?.message ||
        'Failed to confirm booking. Please contact support.'
      );
    }
  };

  const handlePaymentError = (error: string) => {
    setBookingError(error);
  };

  // Loading state for therapist and slots
  if (therapistLoading || slotsLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Spinner size="lg" />
          <p className="mt-4 text-gray-600">Loading booking information...</p>
        </div>
      </div>
    );
  }

  // Error loading therapist
  if (therapistError || !therapist) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md text-center">
          <h3 className="text-red-800 font-semibold mb-2">Therapist Not Found</h3>
          <p className="text-red-600 text-sm">
            Unable to load therapist information. They may no longer be available.
          </p>
          <button
            onClick={() => navigate('/therapists')}
            className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors text-sm font-medium"
          >
            Find Another Therapist
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeftIcon className="h-5 w-5" />
            Back
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Book a Session</h1>
          <p className="mt-2 text-gray-600">
            with {therapist.firstName} {therapist.lastName}
          </p>
        </div>

        {/* Progress Steps */}
        {currentStep !== 'confirmation' && (
          <div className="mb-8">
            <div className="flex items-center justify-between">
              {steps.map((step, index) => (
                <div
                  key={step.key}
                  className={clsx(
                    'flex flex-col items-center flex-1',
                    index !== steps.length - 1 && 'relative'
                  )}
                >
                  <div
                    className={clsx(
                      'w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-colors',
                      index <= currentStepIndex
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-200 text-gray-500'
                    )}
                  >
                    {index + 1}
                  </div>
                  <span
                    className={clsx(
                      'mt-2 text-sm font-medium',
                      index <= currentStepIndex ? 'text-indigo-600' : 'text-gray-500'
                    )}
                  >
                    {step.label}
                  </span>
                  {index !== steps.length - 1 && (
                    <div
                      className={clsx(
                        'absolute top-5 h-0.5 transition-colors',
                        'left-1/2 right-0',
                        index < currentStepIndex ? 'bg-indigo-600' : 'bg-gray-200'
                      )}
                      style={{ width: 'calc(100% - 2.5rem)' }}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Error Banner */}
        {bookingError && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm text-red-800">{bookingError}</p>
          </div>
        )}

        {/* Main Content */}
        <div className="bg-white rounded-lg shadow-lg p-6 md:p-8">
          {/* Step 1: Slot Selection */}
          {currentStep === 'slot-selection' && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Select Date and Time
              </h2>
              {Object.keys(availableSlots).length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-600">No available time slots in the next 4 weeks.</p>
                  <p className="text-gray-500 text-sm mt-2">
                    This therapist may not have availability set up yet.
                  </p>
                </div>
              ) : (
                <SlotSelector
                  availableSlots={availableSlots}
                  selectedDate={bookingState.selectedDate}
                  selectedTime={bookingState.selectedTime}
                  sessionType={bookingState.sessionType}
                  onDateSelect={(date) =>
                    setBookingState((prev) => ({ ...prev, selectedDate: date, selectedTime: null }))
                  }
                  onTimeSelect={(time) =>
                    setBookingState((prev) => ({ ...prev, selectedTime: time }))
                  }
                  onSessionTypeChange={(type) =>
                    setBookingState((prev) => ({ ...prev, sessionType: type }))
                  }
                />
              )}
            </div>
          )}

          {/* Step 2: Notes */}
          {currentStep === 'notes' && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Add Notes (Optional)
              </h2>
              <div>
                <label
                  htmlFor="notes"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  What would you like to discuss in this session?
                </label>
                <textarea
                  id="notes"
                  rows={6}
                  value={bookingState.notes}
                  onChange={(e) =>
                    setBookingState((prev) => ({ ...prev, notes: e.target.value }))
                  }
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Share any concerns, goals, or topics you'd like to address..."
                />
                <p className="mt-2 text-sm text-gray-500">
                  This information will be shared with your therapist before the session.
                </p>
              </div>
            </div>
          )}

          {/* Step 3: Payment */}
          {currentStep === 'payment' && bookingState.clientSecret && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Payment</h2>
              <Elements stripe={stripePromise}>
                <PaymentForm
                  amount={bookingState.amount || 0}
                  platformFee={bookingState.platformFee || 0}
                  therapistAmount={bookingState.therapistAmount || 0}
                  clientSecret={bookingState.clientSecret}
                  onPaymentSuccess={handlePaymentSuccess}
                  onPaymentError={handlePaymentError}
                  isProcessing={confirmBookingMutation.isPending}
                />
              </Elements>
            </div>
          )}

          {/* Step 4: Confirmation */}
          {currentStep === 'confirmation' && bookingState.bookingId && (
            <BookingConfirmation
              booking={{
                id: bookingState.bookingId,
                slotDate: bookingState.selectedDate!,
                slotTime: bookingState.selectedTime!,
                sessionType: bookingState.sessionType,
                amountCents: bookingState.amount || 0,
              }}
              therapist={{
                id: therapist.id,
                firstName: therapist.firstName,
                lastName: therapist.lastName,
                profilePhoto: therapist.profilePhotoS3Key,
              }}
            />
          )}

          {/* Navigation Buttons */}
          {currentStep !== 'payment' && currentStep !== 'confirmation' && (
            <div className="mt-8 flex justify-between gap-4">
              <button
                onClick={handleBack}
                disabled={currentStepIndex === 0}
                className={clsx(
                  'px-6 py-3 font-semibold rounded-lg transition-colors',
                  currentStepIndex === 0
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                )}
              >
                <ArrowLeftIcon className="h-5 w-5 inline mr-2" />
                Back
              </button>

              <button
                onClick={handleNext}
                disabled={!canProceed() || createBookingMutation.isPending}
                className={clsx(
                  'px-6 py-3 font-semibold rounded-lg transition-colors',
                  !canProceed() || createBookingMutation.isPending
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-indigo-600 text-white hover:bg-indigo-700'
                )}
              >
                {createBookingMutation.isPending ? (
                  'Processing...'
                ) : (
                  <>
                    Continue
                    <ArrowRightIcon className="h-5 w-5 inline ml-2" />
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
